import { FilterList as FilterListIcon, PlusOne, } from "@mui/icons-material";
import BusinessIcon from "@mui/icons-material/Business";
import CachedIcon from "@mui/icons-material/Cached";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import {
  Box,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Account } from "../../types/model/account";

interface AccountsTableProps {
  tableData: Account[];
  onRequestRefresh: () => void;
  showAddButton?: boolean;
}

const AccountsTable = ({ 
  tableData,
  onRequestRefresh,
  showAddButton
}: AccountsTableProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({});

  const handleRequestAdd = () => {
    navigate("/accounts/add-account");
  };

  const handleClick = (accountId: number) => {
    navigate(`/accounts/${accountId}`);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const getAccountIcon = (account: Account) => {
    const { isAdministrator, enabled } = account;

    let icon;
    let color;
    let label;

    if (isAdministrator) {
      icon = <SupervisorAccountIcon />;
      color = enabled ? "primary.dark" : "grey.500";
      label = t("generic.administration");
    } else {
      icon = <BusinessIcon />;
      color = enabled ? "primary.main" : "grey.500";
      label = enabled ? t("generic.enabled") : t("generic.disabled");
    }

    return (
      <Tooltip title={label} arrow>
        {React.cloneElement(icon, { sx: { color } })}
      </Tooltip>
    );
  };
  
  const filteredData = tableData.filter((account) => {
    let matches = true;
    for (const key in filters) {
      if (filters[key]) {
        const filterValue = String(filters[key]).toLowerCase();
        const accountValue = String(account[key as keyof Account]).toLowerCase();
        if (!accountValue.includes(filterValue)) {
          matches = false;
          break;
        }
      }
    }
    return matches;
  });

  const visibleRows = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredData.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h4">
            {t("accounts.table.title")}
          </Typography>

          {/* Filter button */}
          <Tooltip title={t("generic.filter")}>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          {/* Refresh button */}
          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={onRequestRefresh}>
              <CachedIcon />
            </IconButton>
          </Tooltip>

          {/* Add account button */}
          {showAddButton && (
            <Tooltip title={t("accounts.table.add-account")}>
              <IconButton onClick={handleRequestAdd}>
                <PlusOne />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>

        {showFilters && (
          <Grid2 container spacing={3} sx={{ mb: 2 }}>
            <Grid2 size={12}>
              <TextField
                name="name"
                label={t("generic.search")}
                value={filters.name ?? ""}
                onChange={handleFilterChange}
                fullWidth
              />
            </Grid2>
          </Grid2>
        )}

        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("accounts.table.name")}</TableCell>
                <TableCell>{t("accounts.table.email")}</TableCell>
                <TableCell>{t("accounts.table.phone-number")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow
                  hover
                  key={row.id}
                  onClick={() =>
                    handleClick(row.id)
                  }
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {getAccountIcon(row)}
                      <Typography variant="body2">{row.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.phoneNumber}</TableCell>
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={3} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default AccountsTable;